/**
 * Hero copy + launch-phase configuration.
 *
 * Why this lives in its own module:
 *   1. Editorial copy stays in one place — designers/writers don't have
 *      to read JSX to find what to edit.
 *   2. Launching the iOS app is a copy + CTA swap. Centralizing it here
 *      makes that swap a single env var change on the host:
 *
 *          # waitlist (default)
 *          VITE_LAUNCH_PHASE=waitlist   →  "Pronto en iOS." + "Avísame cuando esté listo"
 *          # post-launch
 *          VITE_LAUNCH_PHASE=live       →  "Disponible en iOS." + "Descargar Nyx"
 *
 *      No code changes — flip the var in the Cloudflare Pages dashboard,
 *      the auto-redeploy (~60s) flips the copy. Leave `live` until the
 *      App Store URL below is filled in.
 */

export type LaunchPhase = 'waitlist' | 'live';

function readLaunchPhase(): LaunchPhase {
  // Vite client: `import.meta.env.VITE_*` is replaced at build time.
  const fromVite = (import.meta as { env?: Record<string, string | undefined> }).env
    ?.VITE_LAUNCH_PHASE;
  // SSR / prerender (tsx) — `process.env` carries the same value.
  const fromNode =
    typeof process !== 'undefined' && process.env ? process.env['VITE_LAUNCH_PHASE'] : undefined;
  const raw = fromVite ?? fromNode;
  return raw === 'live' ? 'live' : 'waitlist';
}

export const LAUNCH_PHASE: LaunchPhase = readLaunchPhase();

/**
 * One source of truth for hero text. Sentence-level structure matches the
 * editorial spec; line breaks are intentionally absent so the browser
 * reflows naturally based on container width.
 */
export const heroCopy = {
  headline: 'Significados de las canciones que ya cantas de memoria.',
  subhero:
    'Una app para fans que escuchan de cerca. Cómo se mueve un álbum, qué dice cada canción, qué cambia cuando lo ves en vivo.',
  closer: 'En español.',
  availability: {
    waitlist: 'Pronto en iOS.',
    live: 'Disponible en iOS.',
  },
  cta: {
    waitlist: {
      eyebrow: 'Lista de espera',
      label: 'Avísame cuando esté listo',
      href: '#waitlist',
    },
    live: {
      eyebrow: 'App Store',
      label: 'Descargar Nyx',
      // TODO(launch): replace with the real App Store URL.
      href: 'https://apps.apple.com/app/nyx',
    },
  },
  /** Secondary entry point — links to the sample album deep dive. */
  deepDiveLink: (albumTitle: string): string => `Lee el análisis de ${albumTitle}`,
} as const;
